import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Textarea, Table } from "@chakra-ui/react";
import moment from "moment";
import { Trans, useTranslation } from "react-i18next";

import StandardModal from "@/js/components/shared/StandardModalV2";
import { DatePickerFilter } from "@/js/utils/DatePickerFilter";
import NuSingleSelect from "@/js/components/NuSingleSelect";
import { getUserDisplayTextForSelect } from "@/js/utils/userUtils";
import { submitSJRRequest } from "@/js/api/SJRApi";
import iconExportSuccess from "@/images/IconExportSuccess.svg?url";
import iconExportDocument from "@/images/IconExportDocument.svg?url";
import iconTimeSJR from "@/images/IconTimeSJR.svg?url";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { ChakraFAIcon } from "@/components/ui/Icon";

const SJRModal = ({ students, handleClose }) => {
	const { t } = useTranslation();
	const [currentMessage, setCurrentMessage] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [hasSubmitted, setHasSubmitted] = useState(false);
	const [includeLastHour, setIncludeLastHour] = useState(false);
	const [hasIncludeLastHour, setHasIncludeLastHour] = useState(true);
	const [errorStudent, setErrorStudent] = useState("");
	const [errorLastHour, setErrorLastHour] = useState("");
	const [errorDateRange, setErrorDateRange] = useState("");
	const [selectedStudent, setSelectedStudent] = useState("");
	const [startDate, setStartDate] = useState(moment().startOf("day"));
	const [endDate, setEndDate] = useState(moment().endOf("day"));
	const userEmail = useUserSessionStore.getState().getTeacherEmail();

	const handleMessageChange = (evt) => {
		setCurrentMessage(evt.target.value);
	};

	const changeIncludeLastHour = (event) => {
		setIncludeLastHour(event.target.value === "yes");
		setErrorLastHour("");
	};

	const prepareAvailableUsers = () => {
		return students
			.map((student) => {
				student.fname = student.fname || "";
				student.lname = student.lname || "";
				return student;
			})
			.map((student) => ({
				text: getUserDisplayTextForSelect(student),
				value: student.username,
			}));
	};

	const handleSubmit = async () => {
		let error = false;
		setErrorDateRange("");

		if (hasIncludeLastHour && includeLastHour === null) {
			setErrorLastHour(t("studentJourneyReport.errorSelectLastHour"));
			error = true;
		}

		if (selectedStudent === null || selectedStudent === "") {
			setErrorStudent(t("studentJourneyReport.errorSelectStudent"));
			error = true;
		}

		if (!userEmail) {
			setErrorDateRange(t("studentJourneyReport.errorNoEmail"));
			error = true;
		}

		if (error) return;

		setErrorLastHour("");
		setErrorStudent("");
		setIsSubmitting(true);

		try {
			var student = students.filter((student) => student.username === selectedStudent)[0];
			await submitSJRRequest(selectedStudent, {
				user_full_name: `${student.fname} ${student.lname}`.trim(),
				recipient_emails: [userEmail],
				device_name: useUserSessionStore.getState().deviceinfo.user_defined_name,
				user_browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				sort_ascending: false,
				sort_column: "string_datetime",
				comment: currentMessage,
				start_date: startDate.format("YYYY-MM-DD"),
				end_date: endDate.format("YYYY-MM-DD"),
				start_time: startDate.format("HH:mm:ss"),
				end_time: endDate.format("HH:mm:ss"),
				time_zone: useUserSessionStore.getState().deviceinfo.timezone,
				include_last_hour: includeLastHour,
			});
			setIsSubmitting(false);
			setHasSubmitted(true);
			setTimeout(handleClose, 5000);
		} catch (err) {
			if (err.response.status === 504) {
				setErrorDateRange(t("studentJourneyReport.errorTooMuchData"));
			} else {
				setErrorDateRange(t("studentJourneyReport.errorNoActivity"));
			}

			setIsSubmitting(false);
		}
	};

	const handleDateChangeCallback = (start, end) => {
		setStartDate(start);
		setEndDate(end);
		if (start.isSame(moment(), "day") || end.isSame(moment(), "day")) {
			setHasIncludeLastHour(true);
		} else {
			setHasIncludeLastHour(false);
		}
	};

	const handleSetStudent = (studentEmail) => {
		setSelectedStudent(studentEmail);
		setErrorStudent("");
	};

	return (
		<StandardModal open close={handleClose} size="medium" contentClassName="SJRModal" rootClassName="SJRModal_wrapper">
			<Table border="hidden">
				{hasSubmitted && (
					<div className="SJRModal_success">
						<img className="SJRModal_iconSuccess" src={hasSubmitted ? iconExportSuccess : ""} alt="" />
					</div>
				)}
				<div className="SJRModal_header" data-testid="sjr-modal">
					<Box display="flex" justifyContent="center" marginBottom={2} marginTop={3}>
						<h2>
							{hasSubmitted ? (
								<div className="SJRModal_successText">
									<Trans i18nKey="studentJourneyReport.successTitle" components={{ br: <br /> }} />
								</div>
							) : (
								t("studentJourneyReport.modalTitle")
							)}
						</h2>
					</Box>
					{hasSubmitted ? <div className="SJRModal_successEmail">{userEmail}</div> : t("studentJourneyReport.intro")}
				</div>
				{hasSubmitted && (
					<Box display="flex" justifyContent="center" className="SJRModal_lastHour">
						<div className="SJRModal_lastHourText SJRModal_successText">
							{includeLastHour ? (
								<img src={iconTimeSJR} className="SJRModal_lastHourImgIcon" alt="" />
							) : (
								<ChakraFAIcon icon="fa-info-circle" variant="solid" className="SJRModal_lastHourIcon" />
							)}

							{includeLastHour ? t("studentJourneyReport.lastHourProcessing") : t("studentJourneyReport.largeDataProcessing")}
						</div>
					</Box>
				)}
				{!hasSubmitted && (
					<div className="SJRModal_item">
						<NuSingleSelect
							allItems={prepareAvailableUsers()}
							searchPlaceholder={t("studentJourneyReport.searchPlaceholder")}
							title={t("studentJourneyReport.selectStudentTitle")}
							onChange={handleSetStudent}
						/>
						{errorStudent && <div className="error">{errorStudent}</div>}
					</div>
				)}
				<div className="SJRModal_item">
					<div className="SJRModal_itemText">{t("studentJourneyReport.dateRange")}</div>
					<div className="SJRModal_itemForm SJRModal_date-picker">
						<DatePickerFilter
							additionalClasses="SJRModal_date-range"
							startDate={startDate}
							endDate={endDate}
							maxDate={moment()}
							onCallback={handleDateChangeCallback}
							opens="right"
						/>
					</div>
					{errorDateRange && <div className="error">{errorDateRange}</div>}
				</div>
				{hasIncludeLastHour && (
					<div className="SJRModal_item" onChange={changeIncludeLastHour}>
						<div className="SJRModal_itemText">{t("studentJourneyReport.includeLastHour")}</div>
						<div className="SJRModal_itemForm SJRModal_itemFormGroup">
							<div className="SJRModal_itemFormElement">
								<input type="radio" value="yes" name="lasthour" checked={includeLastHour ? "checked" : ""} readOnly />
								{t("studentJourneyReport.yes")}
							</div>
							<div className="SJRModal_itemFormElement">
								<input type="radio" value="no" name="lasthour" checked={!includeLastHour ? "checked" : ""} readOnly />
								{t("studentJourneyReport.no")}
							</div>
						</div>

						{errorLastHour && <div className="error">{errorLastHour}</div>}

						{includeLastHour && (
							<div className="SJRModal_lastHour">
								<Box className="SJRModal_lastHourText" display="flex" alignItems="center">
									<img src={iconTimeSJR} className="SJRModal_lastHourImgIcon" alt="" />
									{t("studentJourneyReport.lastHourProcessing")}
								</Box>
							</div>
						)}
					</div>
				)}
				<div className="SJRModal_item">
					<div className="SJRModal_itemText">{t("studentJourneyReport.coverPageComment")}</div>
					<div className="SJRModal_itemForm">
						<Textarea
							sx={{ height: "134px" }}
							className="SJRModal_textArea"
							name="messageBody"
							onChange={handleMessageChange}
							value={currentMessage}
						/>
					</div>
				</div>
				<div className="SJRModal_footer">
					<button
						disabled={isSubmitting}
						onClick={handleSubmit}
						className="SJRModal_submitButton MuiButtonBase-root MuiButton-root MuiButton-text mui-flat-button mui-flat-button blue-text-button"
					>
						{isSubmitting ? (
							<span>
								<div className="SJRModal_buttonLoadingIcon"> </div>
								{t("studentJourneyReport.sendingReport")}
							</span>
						) : (
							<span>
								<img className="SJRModal_buttonIcon" src={iconExportDocument} alt="" />
								{t("studentJourneyReport.export")}
							</span>
						)}
					</button>
					<button onClick={handleClose} className="SJRModal_cancelButton">
						{t("studentJourneyReport.cancel")}
					</button>
				</div>
				{hasSubmitted && <div className="SJRModal_successfooter">{t("studentJourneyReport.successFooter")}</div>}
			</Table>
		</StandardModal>
	);
};

SJRModal.propTypes = {
	handleClose: PropTypes.func.isRequired,
	students: PropTypes.array,
};

export default SJRModal;
